/**
 * Tarih okuma.
 *
 * En pahalı sessiz hata buradaydı. Eski kod `03/04/2026` gördüğünde koşulsuz
 * "3 Nisan" yazıyordu; ABD'li bir kullanıcıda bu tarih "4 Mart" demektir.
 * Kayıt bir ay yanlış döneme düşer, aylık rapor ve vergi dönemi bozulur, ve
 * ekranda hiçbir uyarı çıkmaz.
 *
 * İkinci sessiz hata ay adlarındaydı: tanınmayan ad `|| 1` ile Ocak oluyordu.
 * "5. Mai 2026" → 2026-01-05. Burada tanınmayan ad ASLA bir aya dönüşmez;
 * okunamadı denir.
 *
 * Belirsizlik çözüm sırası — tahmin en sonda:
 *   1. Sayının kendisi kanıtlıyor mu?  (25/12 → 25 ay olamaz, GG/AA'dır)
 *   2. Belgenin başka bir tarihi kanıtlıyor mu?  (detectDateOrder)
 *   3. Hesabın yerel ayarı ne diyor?  → değer döner ama ONAYA ÇIKAR
 */

import type { DateOrder, Locale } from './locale.js';
import { type Parsed, sure, unsure, none } from './parsed.js';

/* Ay adları — kısaltma ve tam ad, aksan/Türkçe harf sadeleştirilerek.
   Tanınmayan ad buraya girmez ve okunamadı sayılır; sessizce Ocak olmaz. */
const MONTH_4: Readonly<Record<string, number>> = {
  juin: 6, juil: 7, // fr: 3 harfte ("jui") ayrılamıyor
};
const MONTH_3: Readonly<Record<string, number>> = {
  jan: 1, ene: 1, gen: 1, oca: 1,
  feb: 2, fev: 2, sub: 2,
  mar: 3, mrz: 3, mrt: 3,
  apr: 4, abr: 4, avr: 4, nis: 4,
  may: 5, mai: 5, mag: 5, mei: 5,
  jun: 6, giu: 6, haz: 6,
  jul: 7, lug: 7, tem: 7,
  aug: 8, ago: 8, agu: 8,
  sep: 9, set: 9, eyl: 9,
  oct: 10, okt: 10, ott: 10, out: 10, eki: 10,
  nov: 11, kas: 11,
  dec: 12, dez: 12, dic: 12, ara: 12,
};

/**
 * Aksanları ve Türkçe'ye özgü harfleri sadeleştir.
 *
 * Kaynakta ASCII dışı karakter YOK: birleşik aksan işaretleri ve noktasız ı
 * kaçış dizisiyle yazıldı. Görünmez karakterler dosyadan dosyaya taşınırken
 * sessizce bozulabiliyor; bu satırların doğruluğu gözle denetlenebilmeli.
 */
function fold(s: string): string {
  return s
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i').replace(/İ/g, 'i')
    .toLowerCase();
}

/** Ay adını numaraya çevir. Tanınmazsa 0 — asla varsayılan ay yok. */
export function monthFromName(name: string): number {
  const f = fold(String(name).trim());
  return MONTH_4[f.slice(0, 4)] ?? MONTH_3[f.slice(0, 3)] ?? 0;
}

function expandYear(y: number): number {
  if (y >= 100) return y;
  return y <= 68 ? 2000 + y : 1900 + y;
}

function valid(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

function iso(y: number, m: number, d: number): string {
  return String(y).padStart(4, '0') + '-' + String(m).padStart(2, '0') + '-' + String(d).padStart(2, '0');
}

const NUMERIC = /^(\d{1,4})[.\-\/\s](\d{1,2})[.\-\/\s](\d{1,4})$/;
const NAMED_DM = /^(\d{1,2})[.\s-]*([A-Za-zÀ-ÿĞğİıŞş]{3,12})\.?[\s,-]*(\d{2,4})$/;
const NAMED_MD = /^([A-Za-zÀ-ÿĞğİıŞş]{3,12})\.?[\s,-]*(\d{1,2})(?:st|nd|rd|th)?[\s,-]+(\d{2,4})$/;

/**
 * Bir belgedeki tarihlere bakıp sırayı KANITLAMAYA çalış.
 *
 * `25/12/2026` gibi tek bir tarih bile belgenin GG/AA olduğunu kanıtlar,
 * çünkü 25 numaralı ay yoktur. Bu, yerel ayara hiç başvurmadan belirsizliği
 * kaldırmanın en ucuz yoludur; belgenin kendi kanıtı hesabın ayarından
 * daha güvenilirdir.
 *
 * @returns Kanıtlanan sıra, kanıt yoksa ya da tarihler çelişiyorsa null.
 */
export function detectDateOrder(samples: readonly string[]): DateOrder | null {
  let dmy = false, mdy = false;
  for (const raw of samples) {
    const m = String(raw).trim().match(NUMERIC);
    if (!m) continue;
    const a = parseInt(m[1], 10), b = parseInt(m[2], 10);
    if (m[1].length === 4) continue;          // YYYY-.. zaten belirsiz değil
    if (a > 12 && a <= 31) dmy = true;        // ilk sayı ay olamaz
    if (b > 12 && b <= 31) mdy = true;        // ikinci sayı ay olamaz
  }
  if (dmy && mdy) return null;                // belge kendiyle çelişiyor
  if (dmy) return 'dmy';
  if (mdy) return 'mdy';
  return null;
}

export interface DateOptions {
  /** detectDateOrder'ın belgeden kanıtladığı sıra. Yerel ayarı ezer. */
  readonly evidence?: DateOrder | null;
}

/**
 * Metinden ISO tarih (YYYY-MM-DD) oku.
 *
 * @param raw     Ham metin.
 * @param locale  Yalnızca kanıt tükendiğinde başvurulur.
 * @param opts    Belgeden çıkan kanıt.
 */
export function parseDate(raw: unknown, locale: Locale, opts: DateOptions = {}): Parsed<string> {
  if (raw === null || raw === undefined || raw === '') return none('empty');

  if (raw instanceof Date) {
    return isNaN(raw.getTime())
      ? none('invalid date')
      : sure(iso(raw.getUTCFullYear(), raw.getUTCMonth() + 1, raw.getUTCDate()));
  }

  const str = String(raw).trim();

  /* ── Ay adı içeren biçimler: sıra belirsizliği yoktur ──────────────── */
  const dm = str.match(NAMED_DM);
  if (dm) {
    const mo = monthFromName(dm[2]);
    if (!mo) return none('month name not recognised: ' + dm[2]);
    const y = expandYear(parseInt(dm[3], 10)), d = parseInt(dm[1], 10);
    return valid(y, mo, d) ? sure(iso(y, mo, d)) : none('invalid date');
  }
  const md = str.match(NAMED_MD);
  if (md) {
    const mo = monthFromName(md[1]);
    if (!mo) return none('month name not recognised: ' + md[1]);
    const y = expandYear(parseInt(md[3], 10)), d = parseInt(md[2], 10);
    return valid(y, mo, d) ? sure(iso(y, mo, d)) : none('invalid date');
  }

  /* ── Sayısal biçimler ──────────────────────────────────────────────── */
  const n = str.match(NUMERIC);
  if (!n) return none('date format not recognised');

  const [a, b, c] = [parseInt(n[1], 10), parseInt(n[2], 10), parseInt(n[3], 10)];

  /* Dört haneli yıl başta → ISO. Belirsizlik yok. */
  if (n[1].length === 4) {
    return valid(a, b, c) ? sure(iso(a, b, c)) : none('invalid date');
  }

  const year = expandYear(c);
  const asDmy = valid(year, b, a) ? iso(year, b, a) : null;
  const asMdy = valid(year, a, b) ? iso(year, a, b) : null;

  /* 1. Sayının kendisi kanıtlıyor mu? Yalnızca bir okuma geçerliyse
     belirsizlik zaten yoktur; yerel ayara hiç başvurulmaz. */
  if (asDmy === null && asMdy === null) return none('invalid date');
  if (asMdy === null) return sure(asDmy as string);
  if (asDmy === null) return sure(asMdy);
  if (asDmy === asMdy) return sure(asDmy);   // 05/05 — hangi sıra olursa olsun aynı

  /* Buradan sonra iki okuma da geçerli ve BİRBİRİNDEN FARKLI. */

  /* 2. Belgenin başka bir tarihi kanıtlıyor mu? Belgenin kendi kanıtı
     hesabın ayarından daha güvenilirdir. */
  if (opts.evidence === 'dmy') return sure(asDmy);
  if (opts.evidence === 'mdy') return sure(asMdy);

  /* 3. Kanıt yok — yerel ayara göre seç, ama ONAYA ÇIKAR. */
  const useDmy = locale.dateOrder !== 'mdy';
  return unsure(
    useDmy ? asDmy : asMdy,
    'Ambiguous day/month order: "' + str + '" can be read both ways.',
    useDmy ? asMdy : asDmy,
  );
}
